import { formatUsd as formatUsdAmount, parseDecimal } from '@/lib/decimal-format'
import { Denomination } from '@/types/generated/common/trading'
import {
  type OrderPreviewRequest,
  type RowPreviewResult,
  WarningCode,
} from '@/types/generated/public_api'

export { formatUsdAmount }

/** Format a Decimal-as-string leverage multiple as "3.20x". "—" when missing/unparseable. */
export function formatLeverageX(value: string | null | undefined): string {
  const d = parseDecimal(value)
  if (d === null) return '—'
  return `${d.toFixed(2)}x`
}

/** Format a Decimal-as-string percentage with an explicit sign as "+5.20%"/"-3.10%". "—" when missing/unparseable. */
export function formatPercentSigned(value: string | null | undefined): string {
  const d = parseDecimal(value)
  if (d === null) return '—'
  const sign = d.gte(0) ? '+' : ''
  return `${sign}${d.toFixed(2)}%`
}

/** Format a Decimal-as-string percentage as "80.25%", unsigned. "—" when missing/unparseable. */
export function formatPercent(value: string | null | undefined): string {
  const d = parseDecimal(value)
  if (d === null) return '—'
  return `${d.toFixed(2)}%`
}

/**
 * Renders a current → after transition. Both sides use the formatter's own
 * missing-input handling ("—") rather than a caller-chosen fallback like "0".
 */
export function formatTransition(
  current: string | null | undefined,
  after: string | null | undefined,
  formatter: (value: string | null | undefined) => string
): string {
  return `${formatter(current)} → ${formatter(after)}`
}

/**
 * Prefer the current → after margin-utilisation levels from newer API responses,
 * while preserving the signed impact supplied by older deployments.
 */
export function formatMarginUsage(
  current: string | null | undefined,
  after: string | null | undefined,
  impact: string | null | undefined
): string {
  if (current == null && after == null) return formatPercentSigned(impact)
  return formatTransition(current, after, formatPercent)
}

/**
 * Splits a maxSize amount (denominated per the preview request, base or
 * quote) into both legs, deriving price from the same preview's own
 * orderValueBase/orderValueQuote ratio so the two figures stay consistent
 * with the rest of the panel. The already-known leg passes through as-is;
 * the derived leg is null (renders "—") when the ratio isn't available.
 */
export function splitMaxSize(
  maxSize: string | null | undefined,
  denomination: Denomination,
  orderValueBase: string | null | undefined,
  orderValueQuote: string | null | undefined
): { base: string | null; quote: string | null } {
  const size = parseDecimal(maxSize)
  if (size === null) return { base: null, quote: null }

  const base = parseDecimal(orderValueBase)
  const quote = parseDecimal(orderValueQuote)
  const price = base && quote && !base.isZero() ? quote.div(base) : null

  if (denomination === Denomination.DENOMINATION_QUOTE) {
    return {
      base: price && !price.isZero() ? size.div(price).toString() : null,
      quote: size.toString(),
    }
  }
  return { base: size.toString(), quote: price ? size.mul(price).toString() : null }
}

/** Maps a WarningCode to its i18n message key (orderForm namespace). Null when no warning. */
export function warningMessageKey(
  code: WarningCode | null | undefined
): 'warningInsufficientMargin' | 'warningHighLiquidationRisk' | null {
  switch (code) {
    case WarningCode.WARNING_CODE_INSUFFICIENT_MARGIN:
      return 'warningInsufficientMargin'
    case WarningCode.WARNING_CODE_HIGH_LIQUIDATION_RISK:
      return 'warningHighLiquidationRisk'
    default:
      return null
  }
}

/**
 * A liquidation price as an en-US display string: "63,412.12", "0.0001234". Six significant
 * digits (at least 2 dp at or above 1, trailing zeros past that dropped), grouped on the
 * Decimal's own digits. "—" when absent, unparseable or non-positive — the backend omits the
 * field when there is no liquidation price and never sends 0, so a 0 is treated as absent.
 */
export function formatLiquidationPrice(value: string | null | undefined): string {
  const d = parseDecimal(value)
  if (d === null || !d.gt(0)) return '—'
  let fixed: string
  if (d.gte(1)) {
    const intDigits = d.trunc().toFixed().length
    const [intPart, frac = ''] = d.toFixed(Math.max(2, 6 - intDigits)).split('.')
    fixed = `${intPart}.${frac.replace(/0+$/, '').padEnd(2, '0')}`
  } else {
    fixed = d.toSignificantDigits(4).toFixed()
  }
  const [intPart, frac] = fixed.split('.')
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return frac ? `${grouped}.${frac}` : grouped
}

/**
 * Current → after liquidation price, e.g. "~63,412.12 → ~61,000.00". Each side is "—" when
 * absent; `approximate` prefixes "~" only to a side that actually has a value.
 */
export function formatLiquidationTransition(
  current: string | null | undefined,
  after: string | null | undefined,
  approximate: boolean
): string {
  const side = (value: string | null | undefined) => {
    const formatted = formatLiquidationPrice(value)
    return approximate && formatted !== '—' ? `~${formatted}` : formatted
  }
  return `${side(current)} → ${side(after)}`
}

export interface LiquidationPreview {
  /** Set only when the account's rows span 2+ instruments, to label which one this is. */
  symbol?: string
  current?: string
  after?: string
  approximate: boolean
}

/**
 * The liquidation prices to show in one account's preview panel, one per instrument, zipped
 * from the batch request's orders and the response's rows (index-aligned by contract).
 *
 * - One entry per distinct symbol among `accountId`'s successfully-priced rows. With 2+
 *   symbols only those carrying a liquidation price are listed, so a mixed spot/perp ticket
 *   doesn't fill the panel with "—" rows.
 * - `after` is per row (this order alone filling), so a symbol with 2+ rows has no single
 *   "after" to show; it stays absent rather than picking one row's.
 * - No priced rows (loading, or every row failed) → one empty entry, rendering "— → —" like
 *   the panel's other figures before a preview arrives.
 */
export function collectLiquidationPreviews(
  orders: readonly OrderPreviewRequest[] | undefined,
  rows: readonly RowPreviewResult[] | undefined,
  accountId: string
): LiquidationPreview[] {
  const bySymbol = new Map<string, { entry: LiquidationPreview; count: number }>()
  orders?.forEach((order, i) => {
    const ok = rows?.[i]?.ok
    if (!ok || order.accountId !== accountId) return
    const existing = bySymbol.get(order.symbol)
    if (existing) {
      existing.count += 1
      existing.entry.current ??= ok.estimatedLiquidationPriceCurrent
      existing.entry.approximate ||= ok.approximate
      return
    }
    bySymbol.set(order.symbol, {
      count: 1,
      entry: {
        symbol: order.symbol,
        current: ok.estimatedLiquidationPriceCurrent,
        after: ok.estimatedLiquidationPriceAfter,
        approximate: ok.approximate,
      },
    })
  })
  const entries = [...bySymbol.values()].map(({ entry, count }) =>
    count > 1 ? { ...entry, after: undefined } : entry
  )
  if (entries.length === 0) return [{ approximate: false }]
  if (entries.length === 1) return [{ ...entries[0], symbol: undefined }]
  const withValue = entries.filter((e) => e.current != null || e.after != null)
  return withValue.length > 0 ? withValue : [{ approximate: false }]
}
